import React, { useState } from 'react';
import './CodeBlock.css';

const CodeBlock = ({ code, language = 'javascript', filename = null, isDiff = false, defaultExpanded = false }) => {
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Function to remove common leading whitespace
  const normalizeCode = (codeStr) => {
    const lines = codeStr.split('\n');
    
    // Find the minimum indentation (excluding empty lines)
    const minIndent = lines
      .filter(line => line.trim().length > 0)
      .reduce((min, line) => {
        const leadingSpaces = line.match(/^(\s*)/)[1].length;
        return Math.min(min, leadingSpaces);
      }, Infinity);
    
    // Remove the common indentation from all lines
    if (minIndent !== Infinity && minIndent > 0) {
      return lines
        .map(line => line.slice(minIndent))
        .join('\n');
    }
    
    return codeStr;
  };

  const normalizedCode = normalizeCode(code);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(normalizedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Parse diff lines if it's a diff
  const renderCode = () => {
    if (!isDiff) {
      return <pre className="code-content">{normalizedCode}</pre>;
    }

    const lines = normalizedCode.split('\n');
    return (
      <pre className="code-content diff">
        {lines.map((line, index) => {
          let lineClass = 'diff-line';
          if (line.startsWith('+')) {
            lineClass += ' diff-add';
          } else if (line.startsWith('-')) {
            lineClass += ' diff-remove';
          } else if (line.startsWith('@@')) {
            lineClass += ' diff-header';
          }
          
          return (
            <div key={index} className={lineClass}>
              {line}
            </div>
          );
        })}
      </pre>
    );
  };

  // Count lines for preview
  const lineCount = normalizedCode.split('\n').length;
  const displayName = filename || `${language} code`;

  return (
    <div className={`code-block ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="code-header">
        <button
          className="expand-button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse code' : 'Expand code'}
        >
          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
          <span className="code-filename">
            <span className="file-icon">📄</span>
            {displayName}
          </span>
          <span className="line-count">({lineCount} lines)</span>
        </button>
        <div className="header-actions">
          {isDiff && <span className="diff-badge">DIFF</span>}
          <button 
            className="copy-button" 
            onClick={copyToClipboard}
            title="Copy code"
          >
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="code-body">
          {renderCode()}
        </div>
      )}
    </div>
  );
};

export default CodeBlock; 